import { Box, Divider, FormHelperText, InputAdornment, Stack, TextField, Typography, useTheme } from '@mui/material';
import { useFormik } from 'formik';
import { t } from 'i18next';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { get, omit } from 'lodash';
import { useMemo, useState } from 'react';
import { ChevronDown, Edit3 } from 'react-feather';
import * as Yup from 'yup';

//Components
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import FlagCountry from 'src/components/common/FlagCountry';
import FormikForm from 'src/components/common/FormikForm';
import IconFeather from 'src/components/common/Icon';
import PopupSelectPhoneCode from 'src/features/Setting/PopupSelectPhoneCode';

// Utilities
import LoadingSkeleton from 'src/components/common/LoadingSkeleton';
import FormItemCountry from 'src/components/Form/FormItemCountry';
import { useNotification } from 'src/hooks/useNotification';
import { useMutateUpdateCustomerInfo } from 'src/services/mutate';
import { useQueryBillingDetail } from 'src/services/queries';
import {
  formatPhoneNumberBeforeSent,
  getCountryByPhoneCode,
  getDefaultPhoneNumber,
  renderTextOrDefault,
} from 'src/utils/helpers/stringHelpers';
import { IBillingDetailForm, IBillingDetailPayload } from 'src/utils/typings/dto/payment';

const BillingDetail = () => {
  const { data, refetch, isFetching } = useQueryBillingDetail();
  const { notification } = useNotification();
  const { mutateAsync, isPending } = useMutateUpdateCustomerInfo();
  const dataBilling = data?.data;
  const [isEdit, setIsEdit] = useState(false);
  const theme = useTheme();
  const [visiblePhoneCode, setVisiblePhoneCode] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const defaultPhoneNumber = useMemo(() => {
    return getDefaultPhoneNumber(dataBilling.phone_number);
  }, [dataBilling.phone_number]);

  const formik = useFormik<IBillingDetailForm>({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      first_name: dataBilling?.first_name || '',
      last_name: dataBilling?.last_name || '',
      email: dataBilling?.email || '',
      company_name: dataBilling?.company_name || '',
      city: dataBilling?.city || '',
      country: dataBilling?.country || '',
      line1: dataBilling?.address || '',
      postal_code: dataBilling?.postal_code || '',
      state: dataBilling?.region || '',
      phone_number: dataBilling.phone_number ? defaultPhoneNumber.phoneNumber : '',
      phone_code: defaultPhoneNumber.phoneCode,
    },
    validationSchema: Yup.object().shape({
      first_name: Yup.string().max(255).required('First name is required'),
      last_name: Yup.string().max(255).required('Last name is required'),
      email: Yup.string().max(255).email('Email is invalid'),
      phone_number: Yup.string()
        .max(255)
        .test('phone_code', 'Phone number is invalid', (text?: string, context?: ExpectedAny) => {
          const code = context?.parent?.phone_code;
          const phoneNumber = code + (text || '');
          if (!text) {
            return true;
          }
          return isValidPhoneNumber(phoneNumber || '');
        }),
      phone_code: Yup.string().max(255),
      company_name: Yup.string().max(255),
      city: Yup.string().max(255),
      country: Yup.string().max(255).required('Country is required'),
      line1: Yup.string().max(255),
      postal_code: Yup.string().max(255),
      state: Yup.string().max(255),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      const phoneNumber = formatPhoneNumberBeforeSent(values.phone_code, values.phone_number);
      const params: IBillingDetailPayload = {
        ...omit(formik.values, ['phone_code', 'phone_number']),
        phone: phoneNumber,
      };
      // Update Customer Billing
      const response = await mutateAsync(params);
      notification(response);
      refetch();
      setIsEdit(false);
      setSubmitting(false);
    },
  });

  const countryCode = useMemo(() => {
    const country = getCountryByPhoneCode(formik.values.phone_code || '');
    return country.code;
  }, [formik.values.phone_code]);

  const getError = (type: string) => {
    return get(formik.touched, type) && Boolean(get(formik.errors, type));
  };

  const renderError = (type: string) => {
    return (
      get(formik.touched, type) &&
      get(formik.errors, type) && (
        <FormHelperText error id={'helper-text-billing-information-' + type}>
          {String(get(formik.errors, type))}
        </FormHelperText>
      )
    );
  };

  const renderFormEditBillingInfo = () => {
    return (
      <Stack direction="column" spacing={2}>
        <Stack direction="row" spacing={1.5}>
          <Box width="100%">
            <TextField
              value={formik.values.first_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(getError('first_name'))}
              id="first_name"
              name="first_name"
              size="small"
              placeholder={t('checkout.billing_information.first_name')}
              label={t('checkout.billing_information.first_name')}
              required
              fullWidth
            />
            {renderError('first_name')}
          </Box>
          <Box width="100%">
            <TextField
              value={formik.values.last_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(getError('last_name'))}
              id="last_name"
              name="last_name"
              size="small"
              placeholder={t('checkout.billing_information.last_name')}
              label={t('checkout.billing_information.last_name')}
              required
              fullWidth
            />
            {renderError('last_name')}
          </Box>
        </Stack>
        <Box>
          <TextField
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={Boolean(getError('email'))}
            id="email"
            name="email"
            size="small"
            placeholder={t('checkout.billing_information.email_address')}
            label={t('checkout.billing_information.email_address')}
            fullWidth
          />
          {renderError('email')}
        </Box>
        <Box width={'100%'}>
          <TextField
            onBlur={formik.handleBlur}
            error={Boolean(getError('phone_number'))}
            id="phone_number"
            name="phone_number"
            value={formik.values.phone_number}
            onChange={formik.handleChange}
            InputProps={{
              startAdornment: (
                <InputAdornment
                  sx={{
                    background: theme.palette.grey[100],
                    height: 'fit-content',
                    padding: '4px 8px',
                    borderRadius: theme.shape.borderRadius / 4,
                  }}
                  onClick={(e) => {
                    setVisiblePhoneCode(true);
                    setAnchorEl(e.currentTarget);
                  }}
                  component={Box}
                  position="start"
                >
                  <FlagCountry width={16} code={countryCode} />
                  <Typography sx={{ ml: 1 }} color={theme.palette.text.secondary} variant="body2">
                    {formik.values['phone_code']}
                  </Typography>
                  <IconFeather muiProps={{ fontSize: 'small', sx: { ml: 1 } }} icon={<ChevronDown />} />
                </InputAdornment>
              ),
            }}
            size="small"
            fullWidth
            label={t('checkout.billing_information.phone_number')}
          />
          {renderError('phone_number')}
        </Box>
        <PopupSelectPhoneCode
          anchorEl={anchorEl}
          id="phone_code"
          open={visiblePhoneCode}
          handleClose={() => setVisiblePhoneCode(false)}
        />

        <Box>
          <TextField
            value={formik.values.company_name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={Boolean(getError('company_name'))}
            id="company_name"
            name="company_name"
            size="small"
            placeholder={t('checkout.billing_information.company_name')}
            label={t('checkout.billing_information.company_name')}
            fullWidth
          />
          {renderError('company_name')}
        </Box>
        <Box>
          <FormItemCountry id="country" label={t('checkout.billing_information.country')} required />
          {renderError('country')}
        </Box>
        <Stack direction="row" spacing={1.5}>
          <Box width="100%">
            <TextField
              value={formik.values.state}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(getError('state'))}
              id="state"
              name="state"
              size="small"
              placeholder={t('checkout.billing_information.region')}
              label={t('checkout.billing_information.region')}
              fullWidth
            />
            {renderError('state')}
          </Box>
          <Box width="100%">
            <TextField
              value={formik.values.city}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(getError('city'))}
              id="city"
              name="city"
              size="small"
              placeholder={t('checkout.billing_information.city')}
              label={t('checkout.billing_information.city')}
              fullWidth
            />
            {renderError('city')}
          </Box>
        </Stack>
        <Stack direction="row" spacing={1.5}>
          <Box width="100%">
            <TextField
              value={formik.values.line1}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(getError('line1'))}
              id="line1"
              name="line1"
              size="small"
              placeholder={t('checkout.billing_information.street_address')}
              label={t('checkout.billing_information.street_address')}
              fullWidth
            />
            {renderError('line1')}
          </Box>
          <Box width="100%">
            <TextField
              value={formik.values.postal_code}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(getError('postal_code'))}
              id="postal_code"
              name="postal_code"
              size="small"
              placeholder={t('checkout.billing_information.zip')}
              label={t('checkout.billing_information.zip')}
              fullWidth
            />
            {renderError('postal_code')}
          </Box>
        </Stack>
      </Stack>
    );
  };

  if (isFetching) {
    return <LoadingSkeleton count={5} height={30} />;
  }

  return (
    <FormikForm formik={formik}>
      <Stack direction="column" spacing={2}>
        <Box>
          <Typography variant="h6">{t('setting.plan_usage_manage.billing_detail.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('setting.plan_usage_manage.billing_detail.sub_title')}
          </Typography>
        </Box>
        <Divider />
        {isEdit ? (
          renderFormEditBillingInfo()
        ) : (
          <>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {t('setting.plan_usage_manage.billing_detail.company_name')}
              </Typography>
              <Typography variant="subtitle1" align="right">
                {renderTextOrDefault(dataBilling?.company_name)}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {t('setting.plan_usage_manage.billing_detail.full_name')}
              </Typography>
              <Typography variant="subtitle1" align="right">
                {renderTextOrDefault((dataBilling?.first_name || '') + ' ' + (dataBilling?.last_name || ''))}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {t('setting.plan_usage_manage.billing_detail.phone_number')}
              </Typography>
              <Typography variant="subtitle1" align="right">
                {renderTextOrDefault(dataBilling?.phone_number)}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {t('setting.plan_usage_manage.billing_detail.email_address')}
              </Typography>
              <Typography variant="subtitle1" align="right">
                {renderTextOrDefault(dataBilling?.email)}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {t('setting.plan_usage_manage.billing_detail.country')}
              </Typography>
              <Typography variant="subtitle1" align="right">
                {renderTextOrDefault(dataBilling?.country)}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {t('setting.plan_usage_manage.billing_detail.address')}
              </Typography>
              <Typography variant="subtitle1" align="right">
                {renderTextOrDefault(dataBilling?.address)}
              </Typography>
            </Stack>
          </>
        )}
        <Divider />
        <Stack direction="row" justifyContent="flex-end">
          {isEdit ? (
            <ButtonWithLoading
              type="submit"
              variant="contained"
              color="secondary"
              size="medium"
              isLoading={isPending}
              disabled={isPending}
            >
              {t('global.save')}
            </ButtonWithLoading>
          ) : (
            <ButtonWithLoading
              variant="outlined"
              color="secondary"
              size="medium"
              startIcon={
                <IconFeather
                  icon={<Edit3 />}
                  muiProps={{
                    color: 'secondary',
                    fontSize: 'small',
                  }}
                />
              }
              onClick={() => setIsEdit(true)}
            >
              {t('setting.plan_usage_manage.billing_detail.edit_detail')}
            </ButtonWithLoading>
          )}
        </Stack>
      </Stack>
    </FormikForm>
  );
};

export default BillingDetail;
